import type { DownloadTreeNode } from '~/types/download'
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { findTopLevelLawCommentEzId } from '~/utils/solr/law-utils'

interface MapLawTocToDownloadNodesParams {
  tocNodes?: LawAndCommentary[] | null
  comments?: LawAndCommentary[] | null
  lawId?: string | null
  rootTitle?: string
}

interface AnnotateLawTocWithCommentsOptions {
  allowParagraphFallback?: boolean
}

function normalizePath(value: string | null | undefined) {
  if (!value) {
    return ''
  }

  return value
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase()
}

function extractParagraphSegment(value: string) {
  const segments = normalizePath(value).split('/').filter(Boolean)
  return segments.find(segment => segment.startsWith('paragraf-'))
}

function extractPathUntilParagraph(value: string) {
  const segments = normalizePath(value).split('/').filter(Boolean)
  const paragraphIndex = segments.findIndex(segment => segment.startsWith('paragraf-'))

  if (paragraphIndex === -1) {
    return null
  }

  return segments.slice(0, paragraphIndex + 1).join('/')
}

function extractNodeFragmentPath(node: LawAndCommentary, normalizedNodeId: string) {
  if (node.lawFragmentId) {
    return normalizePath(node.lawFragmentId)
  }

  const segments = normalizedNodeId.split('/').filter(Boolean)
  if (segments.length <= 1) {
    return ''
  }

  return segments.slice(1).join('/')
}

function findMatchingComment(
  node: LawAndCommentary,
  comments: LawAndCommentary[],
  options: Required<AnnotateLawTocWithCommentsOptions>,
) {
  const nodeId = node.id
  const normalizedNodeId = normalizePath(nodeId)
  const nodeFragmentPath = extractNodeFragmentPath(node, normalizedNodeId)
  const nodeParagraph = extractParagraphSegment(nodeFragmentPath)
  const nodePathUntilParagraph = extractPathUntilParagraph(nodeFragmentPath)
  const supportsParagraphFallback = node.nodeType === 'paragraph' || node.nodeType === 'section'

  return comments.find((comment) => {
    const normalizedLawId = normalizePath(comment.lawId)
    const normalizedCommentFragment = normalizePath(comment.lawFragmentId)
    const normalizedCommentNodeId = normalizePath(`${normalizedLawId}${normalizedCommentFragment ? `/${normalizedCommentFragment}` : ''}`)

    if (normalizedCommentNodeId === normalizedNodeId) {
      return true
    }

    if (!options.allowParagraphFallback || !supportsParagraphFallback || !nodeParagraph || !normalizedCommentFragment || !nodePathUntilParagraph) {
      return false
    }

    if (extractParagraphSegment(normalizedCommentFragment) !== nodeParagraph) {
      return false
    }

    const commentPathUntilParagraph = extractPathUntilParagraph(normalizedCommentFragment)
    if (!commentPathUntilParagraph) {
      return false
    }

    if (commentPathUntilParagraph === nodePathUntilParagraph) {
      return true
    }

    // Some NS structures include an extra leading path segment in commentary fragments.
    // Allow suffix match only when node path is specific enough to avoid cross-del collisions.
    if (nodePathUntilParagraph.includes('kapittel-')) {
      return commentPathUntilParagraph.endsWith(`/${nodePathUntilParagraph}`)
    }

    return false
  })
}

export function annotateLawTocNodesWithComments(
  tocNodes: LawAndCommentary[],
  comments: LawAndCommentary[],
  options: AnnotateLawTocWithCommentsOptions = {},
): LawAndCommentary[] {
  const mergedOptions: Required<AnnotateLawTocWithCommentsOptions> = {
    allowParagraphFallback: options.allowParagraphFallback ?? true,
  }

  return tocNodes.map((node) => {
    const match = findMatchingComment(node, comments, mergedOptions)
    const children = Array.isArray(node.children)
      ? annotateLawTocNodesWithComments(node.children, comments, mergedOptions)
      : []

    return {
      ...node,
      ezMainNodeId: match?.ezMainNodeId ?? undefined,
      children,
    }
  })
}

function normalizeDownloadNodes(nodes?: LawAndCommentary[] | null): DownloadTreeNode[] {
  if (!nodes?.length) {
    return []
  }

  return nodes.flatMap((node) => {
    const normalizedChildren = normalizeDownloadNodes(node.children as LawAndCommentary[] | undefined)
    const hasDirectComment = typeof node.ezMainNodeId === 'number'

    // Ledd/subsection should only be shown when they have a direct commentary.
    if (node.nodeType === 'subsection' && !hasDirectComment) {
      // Preserve the subsection wrapper when it contains non-subsection children
      // so section/paragraph nodes are not flattened up to the parent level.
      if (normalizedChildren.some(child => child.nodeType !== 'subsection')) {
        return [{
          ...node,
          ezMainNodeId: undefined,
          children: normalizedChildren.length ? normalizedChildren : undefined,
        }]
      }

      return normalizedChildren
    }

    return [{
      ...node,
      ezMainNodeId: node.ezMainNodeId ?? undefined,
      children: normalizedChildren.length ? normalizedChildren : undefined,
    }]
  })
}

export function mapLawTocToDownloadNodes({ tocNodes, comments, lawId, rootTitle = 'Kommentar til loven' }: MapLawTocToDownloadNodesParams): DownloadTreeNode[] {
  const normalizedNodes = normalizeDownloadNodes(tocNodes)
  const rootNode: DownloadTreeNode = {
    id: lawId ?? 'law-root',
    title: rootTitle,
    nodeType: 'law',
    ezMainNodeId: findTopLevelLawCommentEzId(comments, lawId),
    children: [],
  }

  return [rootNode, ...normalizedNodes]
}
